import { LitElement, html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { currentLang } from '../i18n';
import { renderSourceIcon } from '../source-icons';
import { sanitizeHttpUrl } from '../url-sanitizer';
import { provisionalDeadlineCopy } from '../helpers';
import { goalCustomers, goalProject } from '../goal-groupings';
import {
  aiRouteText,
  aiWhoText,
  afterTurn,
  bulkLabels,
  cardTimeline,
  refreshUnavailableShort,
  WORK_GROUPS,
  WORK_KINDS,
  WORK_KIND_ICONS,
  closedText,
  dayGroups,
  dayLabel,
  dueLabel,
  cardDueRow,
  cardFirstReason,
  cardKind,
  cardOverdue,
  firstCards,
  firstLabel,
  goalCards,
  groupLabel,
  isSelectable,
  kindLabel,
  noteText,
  passedProvisionalBundle,
  provisionalBundleText,
  urgentText,
  clockCheckText,
  isAiWaitRow,
  candidateReasonView,
  rowTitle,
  sectionLabel,
  workCount,
  workCounts,
  sectionRows,
  waitingSplit,
  type WorkFilter,
  type WorkGoalCard,
  type WorkItem,
  type WorkKind,
  type WorkList,
  type CardTurn,
  type WorkStep,
} from '../work-list-view';
import { judgmentRefreshNote } from '../goal-detail-view';
import type { GoalDodEdit } from '../goal-dod-rows';
import { defineZenComponent } from './register';

export type WorkListAction =
  | { action: 'filter'; kind: Exclude<WorkFilter, null> }
  /** #3805: narrow the list to one customer's goals; null shows them all again. */
  | { action: 'customer-filter'; domain: string | null; fromSearch?: boolean }
  | { action: 'toggle-low' }
  | { action: 'open'; key: string }
  | { action: 'open-goal'; goalId: string }
  | { action: 'open-task'; goalId: string; subtaskId: string }
  | { action: 'goal-title'; goalId: string; title: string }
  | { action: 'goal-conditions'; goalId: string; text: string }
  | { action: 'goal-condition-rows'; goalId: string; edits: GoalDodEdit[] }
  | { action: 'goal-feedback'; goalId: string; text: string }
  | { action: 'close-detail' }
  | { action: 'search-open'; goalId: string; subtaskId: string | null }
  | { action: 'achievement-close' }
  | { action: 'approve'; subtaskId: string; resultContent: string | null }
  | { action: 'feedback'; goalId: string; subtaskId: string; taskTitle: string; text: string; redo: boolean }
  | { action: 'complete-task'; subtaskId: string }
  | { action: 'executor'; subtaskId: string; executor: 'human' | 'ai' }
  | { action: 'not-needed'; subtaskId: string; title: string }
  | { action: 'task-deadline'; subtaskId: string; element: HTMLElement; current: string | null }
  | { action: 'task-condition'; subtaskId: string; index: number; done: boolean }
  | { action: 'check-step'; subtaskId: string }
  | { action: 'undo-step'; subtaskId: string }
  | { action: 'check-goal'; goalId: string }
  | { action: 'goal-deadline'; goalId: string; day: string }
  | { action: 'goal-deadline-bulk'; goalIds: string[]; day: string }
  | { action: 'goal-deadline-pick'; goalId: string; element: HTMLElement; current: string }
  | { action: 'choose'; key: string; goalId: string; optionKey: string; optionValue: string; token: string }
  | { action: 'choose-text'; key: string; goalId: string; optionKey: string; optionValue: string; token: string; text: string }
  | { action: 'candidate'; candidateId: string; accept: boolean }
  | { action: 'completion'; goalId: string; completionId: string; accept: boolean }
  | { action: 'reopen'; goalId: string; proposalId: string; accept: boolean }
  | { action: 'undo-goal-done'; goalId: string }
  | { action: 'goal-link'; holdId: string; goalIds: string[] }
  | { action: 'goal-link-new'; holdId: string }
  | { action: 'goal-link-dismiss'; holdId: string }
  | { action: 'close-proposal'; subtaskId: string; accept: boolean }
  /** Owner 2026-10-28: 「外す」 hands the hold's release to the AI, which takes it off the calendar. */
  | { action: 'release-hold'; goalId: string; eventId: string; subtaskId: string }
  /** #3222: a change of turn told from the goal's bottom box, with what was said. */
  | { action: 'goal-turn-change'; goalId: string; turn: 'counterparty' | 'principal'; text: string }
  | { action: 'actor-answer'; key: string; goalId: string; conditionRef: string; actor: 'principal' | 'teammate' | 'external_party'; optionKey: string; teammateUserId?: string }
  | { action: 'select'; key: string; selected: boolean }
  | { action: 'bulk'; bulk: 'all' | 'clear' | 'accept' | 'dismiss' }
  /** Yoshie 2026-10-29: choose goal cards and complete them, or drop them as not needed, together. */
  | { action: 'select-cards'; on: boolean }
  | { action: 'card-bulk'; bulk: 'all' | 'done' | 'dismiss'; keys?: string[] };

/**
 * #2749: tiles, then 優先 (the pressing goals and judgments together), then
 * one section per kind in date order. Judgments sit under
 * one sub-heading per group, and the low-priority ones fold at the end. Goal
 * candidates and completion proposals carry a check to approve them together.
 */
export class ZenWorkList extends LitElement {
  static properties = {
    list: { attribute: false },
    filter: { attribute: false },
    lowExpanded: { attribute: false },
    selected: { attribute: false },
    busy: { attribute: false },
    leaving: { attribute: false },
    chosen: { attribute: false },
    openKey: { attribute: false },
    selecting: { attribute: false },
    customerFilter: { attribute: false },
    customersRead: { attribute: false },
    provisionalOpen: { state: true },
  };

  declare list: WorkList | null;
  declare filter: WorkFilter;
  declare lowExpanded: boolean;
  /** Item keys chosen for the bulk bar. */
  declare selected: Set<string>;
  /** Subtask and Goal ids just checked, shown as done until the list is read again. */
  declare busy: Set<string>;
  /** #3549: keys of rows just answered, playing their exit before they leave. */
  declare leaving: Set<string>;
  /** Judgment rows just answered (row key → option key), shown chosen until the list is read again. */
  declare chosen: Map<string, string>;
  /** The row whose detail is open beside the list. */
  declare openKey: string | null;
  /** Goal cards show a selection check in place of the done check (Yoshie 2026-10-29). */
  declare selecting: boolean;
  /** Whether the passed provisional deadlines (#3064) are shown one by one. */
  declare provisionalOpen: boolean;
  /** #3805: the customer domain the list is narrowed to. */
  declare customerFilter: string | null;
  /** #3805: changes when the customers arrive, so the cards draw their marks. */
  declare customersRead: number;
  /** The goals ticked on each held thread message, by hold id, until it is recorded. */
  private linkChoices = new Map<string, Set<string>>();

  constructor() {
    super();
    this.list = null;
    this.filter = null;
    this.lowExpanded = false;
    this.selected = new Set();
    this.busy = new Set();
    this.leaving = new Set();
    this.chosen = new Map();
    this.openKey = null;
    this.selecting = false;
    this.provisionalOpen = false;
    this.customerFilter = null;
    this.customersRead = 0;
  }

  createRenderRoot() {
    return this;
  }

  private get uiLang() {
    return currentLang === 'en' ? 'en' : 'ja';
  }

  private emit(detail: WorkListAction) {
    this.dispatchEvent(new CustomEvent<WorkListAction>('work-list-action', { detail, bubbles: true, composed: true }));
  }

  private overdueHTML(n: number) {
    const en = this.uiLang === 'en';
    return n > 0 ? html`<span class="work-tile-overdue"><span class="mi mi-xs" aria-hidden="true">error</span>${n} ${en ? 'overdue' : '期限切れ'}</span>` : nothing;
  }

  /**
   * Two tiles: やること (the Goals and the judgments not folded away) and
   * 対応待ち (Yoshie 2026-10-28). やること shows both kinds; its 「ゴール N」
   * and 「判断 N」 each show their own, so the judgments can still be gone
   * through at once.
   */
  private renderMineTile() {
    // Counted in cards: one per Goal (Yoshie 2026-10-29).
    const { task, judgment } = workCounts(this.list!);
    const en = this.uiLang === 'en';
    const label = en ? 'To do' : 'やること';
    const total = workCount(this.list);
    const overdue = task.overdue + judgment.overdue;
    const low = judgment.low ?? 0;
    const active = this.filter === 'mine' || this.filter === 'task' || this.filter === 'judgment';
    const aria = en
      ? `${label} ${total}${overdue ? `, ${overdue} overdue` : ''}${low ? `, ${low} low priority` : ''}`
      : `${label} ${total}件${overdue ? `、期限切れ${overdue}件` : ''}${low ? `、優先度の低い判断${low}件` : ''}`;
    const part = (kind: 'task' | 'judgment', n: number) => {
      const on = this.filter === kind;
      const partAria = en ? `Only ${kindLabel(kind, 'en')}, ${n}` : `${kindLabel(kind, 'ja')}だけ ${n}件`;
      return html`<button type="button" class=${`work-tile-part work-tile-part-${kind}${on ? ' active' : ''}`} aria-pressed=${on ? 'true' : 'false'} aria-label=${partAria}
        @click=${() => this.emit({ action: 'filter', kind })}>${kindLabel(kind, this.uiLang)} ${n}</button>`;
    };
    return html`
      <div class=${`work-tile work-tile-mine${active ? ' active' : ''}`}>
        <button type="button" class="work-tile-main" aria-pressed=${this.filter === 'mine' ? 'true' : 'false'} aria-label=${aria}
          @click=${() => this.emit({ action: 'filter', kind: 'mine' })}>
          <span class="work-tile-head"><span class="mi mi-xs" aria-hidden="true">checklist</span>${label}</span>
          <span class="work-tile-count" aria-hidden="true">${total}${low ? html`<span class="work-tile-low" title=${en ? 'Low priority' : '優先度の低い判断'}><span class="mi mi-xs" aria-hidden="true">low_priority</span>${en ? `+${low} low priority` : `ほか低優先 ${low}`}</span>` : nothing}</span>
          <span class="work-tile-foot" aria-hidden="true">${this.overdueHTML(overdue)}</span>
        </button>
        <span class="work-tile-parts">${part('task', task.count)}<span class="work-tile-part-sep" aria-hidden="true">・</span>${part('judgment', judgment.count)}</span>
      </div>
    `;
  }

  private renderWaitingTile() {
    const s = this.list!.summary.waiting;
    const en = this.uiLang === 'en';
    const active = this.filter === 'waiting';
    const split = s.count > 0 ? waitingSplit(s, this.uiLang) : '';
    const aria = en
      ? `${kindLabel('waiting', 'en')} ${s.count}${s.overdue ? `, ${s.overdue} overdue` : ''}${split ? `, ${split}` : ''}`
      : `${kindLabel('waiting', 'ja')} ${s.count}件${s.overdue ? `、期限切れ${s.overdue}件` : ''}${split ? `、${split}` : ''}`;
    return html`
      <button type="button" class=${`work-tile work-tile-waiting${active ? ' active' : ''}`} aria-pressed=${active ? 'true' : 'false'} aria-label=${aria}
        @click=${() => this.emit({ action: 'filter', kind: 'waiting' })}>
        <span class="work-tile-head"><span class="mi mi-xs" aria-hidden="true">${WORK_KIND_ICONS.waiting}</span>${kindLabel('waiting', this.uiLang)}</span>
        <span class="work-tile-count" aria-hidden="true">${s.count}</span>
        <span class="work-tile-foot" aria-hidden="true">
          ${this.overdueHTML(s.overdue)}
          ${split ? html`<span class="work-tile-split">${split}</span>` : nothing}
        </span>
      </button>
    `;
  }

  /** `withIcon`: a goal card leads its date with the calendar mark, as its other marks are icons. */
  private renderDue(item: WorkItem, withIcon = false) {
    const due = dueLabel(item, this.list!.today, this.uiLang);
    const provisional = item.due?.provisional ? provisionalDeadlineCopy(this.uiLang) : null;
    const lead = item.overdue ? 'error' : withIcon && item.due ? 'event' : null;
    return html`<span class="work-due" aria-label=${due.aria} title=${provisional ? provisional.title : nothing}>${lead ? html`<span class="mi mi-xs" aria-hidden="true">${lead}</span>` : nothing}<span aria-hidden="true">${due.text}</span>${provisional ? html`<span class="deadline-provisional" aria-hidden="true">${provisional.tag}</span>` : nothing}</span>`;
  }

  private renderSource(item: WorkItem) {
    if (!item.source) return nothing;
    const icon = renderSourceIcon(item.source.url, item.source.type, 'xs');
    const url = sanitizeHttpUrl(item.source.url);
    if (!url) return html`<span class="work-source-icon">${icon}</span>`;
    const label = this.uiLang === 'en' ? 'Open the source' : '元のメッセージを開く';
    return html`<a class="work-source-icon" href=${url} title=${label} aria-label=${label} target="_blank" rel="noopener noreferrer">${icon}</a>`;
  }

  private renderCheck(id: string, title: string, onCheck: () => void, extraClass = '') {
    const done = this.busy.has(id);
    const label = this.uiLang === 'en' ? `Mark "${title}" done` : `「${title}」を完了にする`;
    return html`<button type="button" class=${`subtask-check work-check${extraClass}${done ? ' checked' : ''}`} ?disabled=${done} aria-label=${label} @click=${onCheck}>${done ? html`<span class="mi mi-xs" aria-hidden="true">check</span>` : nothing}</button>`;
  }

  private renderTitle(item: WorkItem) {
    const open = this.openKey === item.key;
    return html`<button type="button" class="work-title" data-work-key=${item.key} aria-expanded=${open ? 'true' : 'false'} aria-controls="workDetail"
      @click=${() => this.emit({ action: 'open', key: item.key })}>${rowTitle(item, this.uiLang)}</button>`;
  }

  private renderSub(item: WorkItem) {
    const parts: unknown[] = [];
    if (item.waiting?.on === 'ai') parts.push(html`<span class="work-who">${aiWhoText(item.in_progress, this.uiLang)}</span>`);
    const aiWait = isAiWaitRow(item);
    if (aiWait) parts.push(item.title);
    const urgent = urgentText(item, this.uiLang);
    if (urgent) parts.push(html`<span class="work-urgent"><span class="mi mi-xs" aria-hidden="true">priority_high</span>${urgent}</span>`);
    const clock = clockCheckText(item, this.uiLang);
    if (clock) parts.push(html`<span class="work-clock"><span class="mi mi-xs" aria-hidden="true">alarm</span>${clock}</span>`);
    // #2745: a goal candidate says why it is put to the user, not what it would achieve.
    const reason = candidateReasonView(item.note, this.uiLang);
    const note = reason ? '' : noteText(item.note, this.uiLang);
    if (reason) parts.push(html`<span class="work-candidate-reason"><span class="mi mi-xs" aria-hidden="true">${reason.icon}</span>${reason.label}</span>`);
    if (note) parts.push(note);
    if (!aiWait && item.goal_title && item.goal_title !== item.title) parts.push(item.goal_title);
    return html`<span class="work-sub">${this.renderSource(item)}<span class="work-sub-text">${parts.map((part, i) => html`${i > 0 ? ' · ' : ''}${part}`)}</span></span>
      ${this.renderRoute(item)}${this.renderAfter(item)}`;
  }

  /** Owner 2026-10-28: under a judgment, who moves once it is answered (the card's badges). */
  private renderAfter(item: WorkItem) {
    const after = afterTurn(item.after, this.uiLang);
    if (!after) return nothing;
    const en = this.uiLang === 'en';
    return html`<span class="work-after"><span class="work-after-label">${en ? 'Then' : 'そのあと'}</span>${this.renderActor(after.turn)}<span class="work-flow-title">${after.turn.title}</span>${after.more > 0 ? html`<span class="work-after-more">${en ? `+${after.more} more` : `ほか${after.more}件`}</span>` : nothing}</span>`;
  }

  /** #2857: on an AI row, the service and what the AI does there. */
  private renderRoute(item: WorkItem) {
    const text = aiRouteText(item.ai_route, this.uiLang);
    if (!text || !item.ai_route) return nothing;
    return html`<span class="work-route">${renderSourceIcon(null, item.ai_route.where, 'xs')}<span>${text}</span></span>`;
  }

  /** #2951: a place card with no place written: type the place, or leave it empty and go on without travel. */
  private renderTextInput(item: WorkItem) {
    const input = item.text_input;
    if (!input || !item.selection_token) return nothing;
    const token = item.selection_token;
    const sent = this.chosen.get(item.key) ?? null;
    const send = (event: Event) => {
      event.preventDefault();
      const field = (event.currentTarget as HTMLElement).closest('.work-options')?.querySelector('input');
      const text = field?.value.trim() ?? '';
      if (!text) return;
      this.emit({ action: 'choose-text', key: item.key, goalId: item.goal_id, optionKey: input.key, optionValue: input.value, token, text });
    };
    return html`<div class="work-options work-text-option" role="group" aria-label=${item.title}>
      <input type="text" class="work-text-input" maxlength="200" placeholder=${input.placeholder} ?disabled=${sent !== null}
        @keydown=${(event: KeyboardEvent) => { if (event.key === 'Enter' && !event.isComposing) send(event); }}>
      <button type="button" class=${`work-option${sent !== null ? ' chosen' : ''}`} ?disabled=${sent !== null} @click=${send}>${sent !== null ? html`<span class="mi mi-xs" aria-hidden="true">check</span>` : nothing}${input.value}</button>
    </div>`;
  }

  private renderChoices(item: WorkItem) {
    if (item.text_input) return this.renderTextInput(item);
    if (!item.options || !item.selection_token) return nothing;
    const token = item.selection_token;
    const label = this.uiLang === 'en' ? `Choose for "${item.title}"` : `「${item.title}」を選ぶ`;
    // #2857: the choice shows at once; the list catches up after the save.
    const chosen = this.chosen.get(item.key) ?? null;
    return html`<div class="work-options" role="group" aria-label=${label}>
      ${item.options.map((option) => {
        const picked = chosen === option.key;
        return html`<button type="button" class=${`work-option${picked ? ' chosen' : ''}`} aria-pressed=${picked ? 'true' : 'false'} ?disabled=${chosen !== null}
          @click=${() => this.emit({ action: 'choose', key: item.key, goalId: item.goal_id, optionKey: option.key, optionValue: option.value, token })}>${picked ? html`<span class="mi mi-xs" aria-hidden="true">check</span>` : nothing}${option.label}</button>`;
      })}
    </div>`;
  }

  private renderActions(buttons: Array<{ label: string; onClick: (event: Event) => void }>) {
    return html`<div class="work-next-actions" role="group">
      ${buttons.map((button) => html`<button type="button" class="work-option" @click=${(event: Event) => button.onClick(event)}>${button.label}</button>`)}
    </div>`;
  }

  /**
   * #1929 S4: a held thread message. With goals still open it lists them to
   * tick (a message may continue several); either way it can become a new goal
   * or be marked not needed.
   */
  private renderGoalLinkActions(item: WorkItem) {
    const link = item.goal_link!;
    const en = this.uiLang === 'en';
    const holdId = link.hold_id;
    const chosen = this.linkChoices.get(holdId) ?? new Set<string>();
    const toggle = (goalId: string, checked: boolean) => {
      const next = new Set(chosen);
      if (checked) next.add(goalId);
      else next.delete(goalId);
      this.linkChoices.set(holdId, next);
      this.requestUpdate();
    };
    const choices = link.candidate_goals.length > 0
      ? html`<div class="work-link-choices" role="group" aria-label=${en ? 'Goals it may continue' : '続きの候補のゴール'}>
          ${link.candidate_goals.map((goal) => html`<label class="work-link-choice"><input type="checkbox" .checked=${chosen.has(goal.goal_id)}
            @change=${(e: Event) => toggle(goal.goal_id, (e.target as HTMLInputElement).checked)}><span>${goal.title}</span></label>`)}
        </div>`
      : nothing;
    const buttons: Array<{ label: string; onClick: () => void }> = [];
    if (link.candidate_goals.length > 0) {
      buttons.push({
        label: en ? 'Add to the chosen goals' : '選んだゴールに記録する',
        onClick: () => {
          const goalIds = [...(this.linkChoices.get(holdId) ?? [])];
          if (goalIds.length > 0) this.linkChoices.delete(holdId);
          this.emit({ action: 'goal-link', holdId, goalIds });
        },
      });
    }
    buttons.push(
      { label: en ? 'New goal' : '新しいゴールにする', onClick: () => this.emit({ action: 'goal-link-new', holdId }) },
      { label: en ? 'Not needed' : '不要', onClick: () => this.emit({ action: 'goal-link-dismiss', holdId }) },
    );
    return html`${choices}${this.renderActions(buttons)}`;
  }

  private rowActions(item: WorkItem) {
    const en = this.uiLang === 'en';
    if (item.goal_link) return this.renderGoalLinkActions(item);
    if (item.candidate_id) {
      const id = item.candidate_id;
      return this.renderActions([
        { label: en ? 'Make a goal' : 'ゴールにする', onClick: () => this.emit({ action: 'candidate', candidateId: id, accept: true }) },
        { label: en ? 'Not needed' : '不要', onClick: () => this.emit({ action: 'candidate', candidateId: id, accept: false }) },
      ]);
    }
    if (item.completion_id) {
      const completionId = item.completion_id;
      return this.renderActions([
        { label: en ? 'Complete' : '完了にする', onClick: () => this.emit({ action: 'completion', goalId: item.goal_id, completionId, accept: true }) },
        { label: en ? 'Not yet' : 'まだ', onClick: () => this.emit({ action: 'completion', goalId: item.goal_id, completionId, accept: false }) },
      ]);
    }
    if (item.reopen_id) {
      const proposalId = item.reopen_id;
      return this.renderActions([
        { label: en ? 'Reopen' : '再開する', onClick: () => this.emit({ action: 'reopen', goalId: item.goal_id, proposalId, accept: true }) },
        { label: en ? 'Keep it done' : '完了のまま', onClick: () => this.emit({ action: 'reopen', goalId: item.goal_id, proposalId, accept: false }) },
      ]);
    }
    if (item.note?.code === 'provisional_deadline') {
      // #3064 (owner 2026-10-26): the date offered, another date, or done; no answer leaves it undated.
      const day = String(item.note.params.suggested_day ?? '');
      return this.renderActions([
        { label: en ? 'Use that date' : 'その日付にする', onClick: () => this.emit({ action: 'goal-deadline', goalId: item.goal_id, day }) },
        { label: en ? 'Pick another date' : '別の日付にする', onClick: (event) => this.emit({ action: 'goal-deadline-pick', goalId: item.goal_id, element: event.currentTarget as HTMLElement, current: day }) },
        { label: en ? 'Already done' : 'もう済んだ', onClick: () => this.emit({ action: 'check-goal', goalId: item.goal_id }) },
      ]);
    }
    if (item.reply_wait_done) {
      return this.renderActions([
        { label: en ? 'Undo' : '戻す', onClick: () => this.emit({ action: 'undo-goal-done', goalId: item.goal_id }) },
      ]);
    }
    if (item.close_proposal_subtask_id) {
      const subtaskId = item.close_proposal_subtask_id;
      const eventId = item.release_hold_event_id;
      if (eventId) {
        // Owner 2026-10-28: a hold's release says what happens to the hold.
        // 「残す」 keeps the hold, so the release closes without taking it off.
        return this.renderActions([
          { label: en ? 'Remove the hold' : '外す', onClick: () => this.emit({ action: 'release-hold', goalId: item.goal_id, eventId, subtaskId }) },
          { label: en ? 'Keep the hold' : '残す', onClick: () => this.emit({ action: 'close-proposal', subtaskId, accept: true }) },
        ]);
      }
      const notNeeded = item.note?.params.reason === 'not_needed';
      return this.renderActions([
        { label: en ? (notNeeded ? 'Mark not needed' : 'Mark done') : (notNeeded ? '不要にする' : '完了にする'), onClick: () => this.emit({ action: 'close-proposal', subtaskId, accept: true }) },
        { label: en ? 'Keep it' : '残す', onClick: () => this.emit({ action: 'close-proposal', subtaskId, accept: false }) },
      ]);
    }
    if (item.actor_question) {
      // #2732: a judgment like the others: 自分 (with the AI), each teammate by
      // name, or the other side; the choice shows at once (#2857).
      const { condition_ref: conditionRef, teammates } = item.actor_question;
      const chosen = this.chosen.get(item.key) ?? null;
      const options: Array<{ key: string; label: string; actor: 'principal' | 'teammate' | 'external_party'; teammateUserId?: string }> = [
        { key: 'principal', label: en ? 'Me (with the AI)' : '自分（AIと進める）', actor: 'principal' },
        ...teammates.map((member) => ({ key: `teammate:${member.user_id}`, label: member.name, actor: 'teammate' as const, teammateUserId: member.user_id })),
        { key: 'external_party', label: en ? 'The other side' : '相手側', actor: 'external_party' },
      ];
      const label = en ? `Choose for "${item.title}"` : `「${item.title}」を選ぶ`;
      return html`<div class="work-options" role="group" aria-label=${label}>
        ${options.map((option) => {
          const picked = chosen === option.key;
          return html`<button type="button" class=${`work-option${picked ? ' chosen' : ''}`} aria-pressed=${picked ? 'true' : 'false'} ?disabled=${chosen !== null}
            @click=${() => this.emit({
              action: 'actor-answer', key: item.key, goalId: item.goal_id, conditionRef, actor: option.actor, optionKey: option.key,
              ...(option.teammateUserId ? { teammateUserId: option.teammateUserId } : {}),
            })}>${picked ? html`<span class="mi mi-xs" aria-hidden="true">check</span>` : nothing}${option.label}</button>`;
        })}
      </div>`;
    }
    if (item.group === 'next') {
      return this.renderActions([
        { label: en ? 'Complete' : '完了にする', onClick: () => this.emit({ action: 'check-goal', goalId: item.goal_id }) },
        { label: en ? 'See details' : '詳細を見る', onClick: () => this.emit({ action: 'open', key: item.key }) },
      ]);
    }
    return nothing;
  }

  private renderLead(item: WorkItem) {
    if (isSelectable(item)) {
      const label = this.uiLang === 'en' ? `Select "${rowTitle(item, 'en')}"` : `「${rowTitle(item, 'ja')}」を選ぶ`;
      return html`<span class="work-lead"><input type="checkbox" class="work-select" aria-label=${label} .checked=${this.selected.has(item.key)}
        @change=${(e: Event) => this.emit({ action: 'select', key: item.key, selected: (e.target as HTMLInputElement).checked })}></span>`;
    }
    if (item.goal_check) return this.renderCheck(item.goal_id, item.title, () => this.emit({ action: 'check-goal', goalId: item.goal_id }), ' work-goal-check');
    if (item.checkable && item.subtask_id) {
      const id = item.subtask_id;
      return this.renderCheck(id, item.title, () => this.emit({ action: 'check-step', subtaskId: id }));
    }
    const icon = item.executor === 'ai' && item.kind !== 'task' ? 'smart_toy' : WORK_KIND_ICONS[item.kind];
    return html`<span class="work-lead work-lead-icon" aria-hidden="true"><span class="mi mi-sm work-kind-icon">${icon}</span></span>`;
  }

  /** Done / all as the done mark and two numbers (owner 2026-10-25: marks, not words, where a mark says it). */
  private renderProgress(progress: { done: number; total: number }) {
    if (progress.total === 0) return nothing;
    const label = this.uiLang === 'en' ? `${progress.done} of ${progress.total} tasks done` : `タスク${progress.total}件中${progress.done}件済み`;
    return html`<span class="work-progress" role="img" aria-label=${label} title=${label}><span class="mi mi-xs" aria-hidden="true">check_circle</span><span aria-hidden="true">${progress.done}/${progress.total}</span></span>`;
  }

  /**
   * #2857: a click anywhere on a row opens its detail, except on its own
   * controls (checks, the source link, choices). The title stays the
   * keyboard's way in.
   */
  private onRowClick(event: MouseEvent, key: string) {
    const target = event.target as HTMLElement | null;
    if (!target || target.closest('button, a, input, label, select, textarea')) return;
    if (window.getSelection?.()?.toString()) return;
    this.emit({ action: 'open', key });
  }

  /** The badge of whose turn it is, as the detail's timeline draws it. */
  private renderActor(turn: CardTurn) {
    const icon = turn.actor === 'ai' ? 'smart_toy' : turn.actor === 'you' ? 'person' : 'person_outline';
    return html`<span class=${`work-actor work-actor-${turn.actor}`}><span class="mi mi-xs" aria-hidden="true">${icon}</span>${turn.who}</span>`;
  }

  /** One turn on the card: a dot on the line (filled for next), whose turn, and what. */
  private renderTurn(turn: CardTurn, next: boolean, more: number, key: string) {
    const en = this.uiLang === 'en';
    const status = turn.step?.executor === 'ai' && turn.step.in_progress
      ? html`<span class="work-flow-status">${aiWhoText(turn.step.in_progress, this.uiLang)}</span>`
      : turn.since
        ? html`<span class="work-flow-status"><span class="mi mi-xs" aria-hidden="true">hourglass_top</span>${turn.since}</span>`
        : nothing;
    const moreLabel = en ? `See ${more} more` : `ほか ${more} 件を見る`;
    return html`<li class=${`work-flow-row${next ? ' is-next' : ''}`}>
      <span class="work-flow-dot" role="img" aria-label=${next ? (en ? 'Next' : '次') : (en ? 'After that' : 'そのあと')}></span>
      ${this.renderActor(turn)}<span class="work-flow-title">${turn.title}</span>${status}
      ${more > 0 ? html`<button type="button" class="work-more" title=${moreLabel} aria-label=${moreLabel} @click=${() => this.emit({ action: 'open', key })}>+${more}</button>` : nothing}
    </li>`;
  }

  /** A step the latest review closed, on the line before what comes next, with its undo. */
  private renderClosedTurn(step: WorkStep) {
    return html`<li class="work-flow-row is-done">
      <span class="mi mi-sm work-flow-done" aria-hidden="true">check_circle</span>
      <span class="work-flow-title">${step.title}</span>
      <span class="work-closed-note">${closedText(step.closed!, this.uiLang)}<button type="button" class="work-undo" @click=${() => this.emit({ action: 'undo-step', subtaskId: step.subtask_id })}>${this.uiLang === 'en' ? 'Undo' : '戻す'}</button></span>
    </li>`;
  }

  /**
   * The goal card (owner 2026-10-25, 案C-1): the name, its marks (source,
   * done / all, date), then whose turn is next and whose after, on one line
   * of time. The detail beside the list says the rest.
   */
  private renderCard(item: WorkItem) {
    const card = item.card!;
    const en = this.uiLang === 'en';
    // A completed goal's title is struck through and the card leaves the list (#2749).
    const completing = this.busy.has(item.goal_id) || this.leaving.has(item.key);
    const { turns, more } = cardTimeline(item, this.uiLang);
    const refresh = card.judgment_refresh_unavailable ?? null;
    return html`<div class=${`work-row work-task work-card${item.overdue ? ' overdue' : ''}${this.openKey === item.key ? ' open' : ''}${completing ? ' just-completed' : ''}`} role="listitem"
      @click=${(e: MouseEvent) => this.onRowClick(e, item.key)}>
      ${this.renderCheck(item.goal_id, item.title, () => this.emit({ action: 'check-goal', goalId: item.goal_id }), ' work-goal-check')}
      <div class="work-main">${this.renderProject(item.goal_id)}${this.renderTitle(item)}</div>
      <div class="work-side work-card-marks">${this.renderSource(item)}${this.renderCustomers(item.goal_id)}${this.renderProgress(card.progress)}${this.renderDue(item, true)}</div>
      <ol class="work-flow">
        ${card.steps.filter((step) => step.closed).map((step) => this.renderClosedTurn(step))}
        ${turns.map((turn, index) => this.renderTurn(turn, index === 0, index === turns.length - 1 ? more : 0, item.key))}
      </ol>
      ${refresh ? html`<div class="work-card-foot">
        <span class="work-refresh" title=${judgmentRefreshNote(refresh, en)}><span class="mi mi-xs" aria-hidden="true">sync_problem</span>${refreshUnavailableShort(refresh.reason, this.uiLang)}</span>
      </div>` : nothing}
    </div>`;
  }

  /**
   * One judgment on a goal card's line of turns (Yoshie 2026-10-29): the
   * user's turn, its question opening its detail, and its choices or buttons
   * right there.
   */
  private renderAsk(item: WorkItem) {
    const en = this.uiLang === 'en';
    const result = item.group === 'result';
    const turn: CardTurn = { actor: 'you', who: en ? 'You' : 'あなた', title: rowTitle(item, this.uiLang), step: null, since: '' };
    // The AI mark already says it is the AI's work to check.
    const note = result ? '' : noteText(item.note, this.uiLang);
    const clock = clockCheckText(item, this.uiLang);
    const chosen = this.leaving.has(item.key) || (item.subtask_id !== null && this.busy.has(item.subtask_id));
    return html`<li class=${`work-flow-row is-next work-flow-ask${chosen ? ' work-leaving' : ''}`}>
      <span class="work-flow-dot" role="img" aria-label=${en ? 'Next' : '次'}></span>
      ${this.renderActor(turn)}
      <span class="work-flow-title">
        ${result ? html`<span class="mi mi-xs work-flow-kind" title=${groupLabel('result', this.uiLang)} aria-label=${groupLabel('result', this.uiLang)}>smart_toy</span>` : nothing}
        <button type="button" class="work-flow-open" data-work-key=${item.key} aria-expanded=${this.openKey === item.key ? 'true' : 'false'} aria-controls="workDetail"
          @click=${() => this.emit({ action: 'open', key: item.key })}>${turn.title}</button>
        ${clock ? html`<span class="work-clock"><span class="mi mi-xs" aria-hidden="true">alarm</span>${clock}</span>` : nothing}
        ${note ? html`<span class="work-flow-status">${note}</span>` : nothing}
      </span>
      ${this.renderRoute(item)}
      ${item.options || item.text_input || this.hasRowActions(item) ? html`<div class="work-flow-answer">${this.renderChoices(item)}${this.rowActions(item)}</div>` : nothing}
    </li>`;
  }

  private hasRowActions(item: WorkItem) {
    return !!(item.close_proposal_subtask_id || item.actor_question || item.group === 'next');
  }

  /** The AI's results to check, more than two folded into one line that opens the first. */
  private renderAsks(judgments: WorkItem[]) {
    const results = judgments.filter((item) => item.group === 'result');
    if (results.length <= 2) return judgments.map((item) => this.renderAsk(item));
    const en = this.uiLang === 'en';
    const first = results[0]!;
    const rest = results.length - 1;
    const moreLabel = en ? `See ${rest} more` : `ほか ${rest} 件を見る`;
    return judgments.map((item) => {
      if (item.group !== 'result') return this.renderAsk(item);
      if (item !== first) return nothing;
      return html`${this.renderAsk(item)}<li class="work-flow-row work-flow-more"><span class="work-flow-dot" aria-hidden="true"></span>
        <button type="button" class="work-more" title=${moreLabel} aria-label=${moreLabel} @click=${() => this.emit({ action: 'open', key: first.key })}>${en ? `+${rest} more to check` : `ほか ${rest} 件を確認`}</button></li>`;
    });
  }

  /**
   * #3805: the goal's customers, inferred from the addresses of the people
   * its matter is with (dotted, since nobody confirmed them). A click narrows
   * the list to that customer.
   */
  private renderCustomers(goalId: string) {
    const customers = goalCustomers(goalId);
    if (customers.length === 0) return nothing;
    const en = this.uiLang === 'en';
    return customers.map(({ domain, merged_domains: merged }) => {
      const on = this.customerFilter === domain;
      const gathered = merged?.length
        ? (en ? `, gathering ${merged.join(', ')}` : `。${merged.join('、')} をまとめています`)
        : '';
      const label = en
        ? `Customer ${domain} (guessed from addresses${gathered}). ${on ? 'Show all goals' : 'Show only this customer'}`
        : `顧客 ${domain}（アドレスからの推定${gathered}）。${on ? 'すべてのゴールを表示' : 'この顧客のゴールだけを表示'}`;
      return html`<button type="button" class=${`work-customer${on ? ' on' : ''}`} title=${label} aria-label=${label} aria-pressed=${on ? 'true' : 'false'}
        @click=${(e: MouseEvent) => { e.stopPropagation(); this.emit({ action: 'customer-filter', domain: on ? null : domain }); }}>
        <span class="mi mi-xs" aria-hidden="true">apartment</span>${domain}</button>`;
    });
  }

  /** Yoshie 2026-11-03: the 案件 (parent goal) the goal is under, above its name. */
  private renderProject(goalId: string) {
    const project = goalProject(goalId);
    if (!project) return nothing;
    const label = this.uiLang === 'en' ? `Project: ${project.title}` : `案件：${project.title}`;
    return html`<span class="work-project" title=${label}><span class="mi mi-xs" aria-hidden="true">folder</span><span class="work-project-name">${project.title}</span></span>`;
  }

  /** #3805: which customer the list is narrowed to, with the way back to all goals. */
  private renderCustomerBar(shown: number) {
    if (!this.customerFilter) return nothing;
    const en = this.uiLang === 'en';
    const clear = en ? 'Show all goals' : 'すべてのゴールを表示';
    return html`<div class="work-customer-bar" role="status">
      <span class="mi mi-sm" aria-hidden="true">apartment</span>
      <span>${en ? `Customer: ${this.customerFilter}` : `顧客：${this.customerFilter}`}</span>
      <span class="work-customer-note">${shown === 0
        ? (en ? 'No goals with this customer in this view.' : 'この表示にはこの顧客のゴールがありません。')
        : (en ? 'Guessed from the addresses of the people involved.' : '相手のアドレスからの推定です。')}</span>
      <button type="button" class="work-customer-clear" title=${clear} aria-label=${clear}
        @click=${() => this.emit({ action: 'customer-filter', domain: null })}><span class="mi mi-sm" aria-hidden="true">close</span></button>
    </div>`;
  }

  /**
   * One card per Goal (Yoshie 2026-10-29): the goal card as before (done
   * check, name, marks, the line of turns), with the goal's judgments as the
   * user's next turns, so they are no longer rows of their own.
   */
  private renderGoalCard(card: WorkGoalCard) {
    const en = this.uiLang === 'en';
    const { lead, task } = card;
    const plan = task?.card ? task : null;
    const { turns, more } = plan ? cardTimeline(plan, this.uiLang) : { turns: [] as CardTurn[], more: 0 };
    const asks = card.judgments;
    const after = !plan ? afterTurn(asks.find((item) => item.after)?.after, this.uiLang) : null;
    const keys = [card.key, ...asks.map((item) => item.key)];
    const completing = this.busy.has(card.goal_id) || this.leaving.has(card.key);
    const open = this.openKey !== null && keys.includes(this.openKey);
    // Urgent shows on the card even when it sits under 期日超過 or 期限が近い (Yoshie 2026-11-03).
    const urgentLabel = urgentText([...asks, ...(task ? [task] : []), lead].find((item) => urgentText(item, this.uiLang)) ?? lead, this.uiLang);
    const urgent = !!urgentLabel;
    const refresh = plan?.card?.judgment_refresh_unavailable ?? null;
    const title = lead.goal_title || lead.title;
    return html`<div class=${`work-row work-task work-card${cardOverdue(card) ? ' overdue' : ''}${open ? ' open' : ''}${completing ? ' just-completed' : ''}${this.selecting && this.selected.has(card.key) ? ' selected' : ''}`} role="listitem"
      @click=${(e: MouseEvent) => this.onRowClick(e, card.key)}>
      ${this.selecting ? this.renderCardSelect(card, title) : this.renderCheck(card.goal_id, title, () => this.emit({ action: 'check-goal', goalId: card.goal_id }), ' work-goal-check')}
      <div class="work-main">${this.renderProject(card.goal_id)}<button type="button" class="work-title" data-work-key=${card.key} aria-expanded=${this.openKey === card.key ? 'true' : 'false'} aria-controls="workDetail"
        @click=${() => this.emit({ action: 'open', key: card.key })}>${title}</button></div>
      <div class="work-side work-card-marks">
        ${this.renderSource(lead)}
        ${this.renderCustomers(card.goal_id)}
        ${plan ? this.renderProgress(plan.card!.progress) : nothing}
        ${urgent && urgentLabel ? html`<span class="work-urgent-mark" role="img" title=${urgentLabel} aria-label=${urgentLabel}><span class="mi mi-xs" aria-hidden="true">priority_high</span></span>` : nothing}
        ${this.renderDue(cardDueRow(card), true)}
      </div>
      <ol class="work-flow">
        ${plan ? plan.card!.steps.filter((step) => step.closed).map((step) => this.renderClosedTurn(step)) : nothing}
        ${this.renderAsks(asks)}
        ${turns.map((turn, index) => this.renderTurn(turn, asks.length === 0 && index === 0, index === turns.length - 1 ? more : 0, card.key))}
        ${after ? this.renderTurn(after.turn, false, after.more, card.key) : nothing}
      </ol>
      ${refresh ? html`<div class="work-card-foot">
        <span class="work-refresh" title=${judgmentRefreshNote(refresh, en)}><span class="mi mi-xs" aria-hidden="true">sync_problem</span>${refreshUnavailableShort(refresh.reason, this.uiLang)}</span>
      </div>` : nothing}
    </div>`;
  }

  private renderCardSelect(card: WorkGoalCard, title: string) {
    const label = this.uiLang === 'en' ? `Select "${title}"` : `「${title}」を選ぶ`;
    return html`<span class="work-lead"><input type="checkbox" class="work-select work-card-select" aria-label=${label} .checked=${this.selected.has(card.key)}
      @change=${(e: Event) => this.emit({ action: 'select', key: card.key, selected: (e.target as HTMLInputElement).checked })}></span>`;
  }

  /**
   * Yoshie 2026-10-29: 「選ぶ」 turns the cards' done checks into selection
   * checks; the chosen goals are completed, or dropped as not needed, together.
   */
  private renderCardBar(cards: WorkGoalCard[]) {
    if (cards.length === 0) return nothing;
    const en = this.uiLang === 'en';
    if (!this.selecting) {
      return html`<div class="work-bulk-bar work-card-bar"><button type="button" class="work-bulk-btn" @click=${() => this.emit({ action: 'select-cards', on: true })}>
        <span class="mi mi-xs" aria-hidden="true">checklist</span>${en ? 'Select goals' : 'ゴールを選ぶ'}</button></div>`;
    }
    const chosen = cards.filter((card) => this.selected.has(card.key)).length;
    return html`<div class="work-bulk-bar work-card-bar active" role="group" aria-label=${en ? 'Act on the selected goals' : '選んだゴールをまとめて操作'}>
      <span class="work-bulk-count" aria-live="polite">${en ? `${chosen} selected` : `${chosen}件を選択中`}</span>
      <button type="button" class="work-bulk-btn" @click=${() => this.emit({ action: 'card-bulk', bulk: 'all', keys: cards.map((card) => card.key) })}>${en ? 'Select all' : 'すべて選択'}</button>
      <span class="work-section-gap"></span>
      <button type="button" class="work-bulk-btn work-bulk-primary" ?disabled=${chosen === 0} @click=${() => this.emit({ action: 'card-bulk', bulk: 'done' })}>${en ? 'Complete all' : 'まとめて完了'}</button>
      <button type="button" class="work-bulk-btn" ?disabled=${chosen === 0} @click=${() => this.emit({ action: 'card-bulk', bulk: 'dismiss' })}>${en ? 'Not needed' : 'まとめて不要'}</button>
      <button type="button" class="work-bulk-btn work-bulk-clear" aria-label=${en ? 'Stop selecting' : '選ぶのをやめる'} @click=${() => this.emit({ action: 'select-cards', on: false })}><span class="mi mi-sm" aria-hidden="true">close</span></button>
    </div>`;
  }

  private renderCards(cards: WorkGoalCard[]) {
    return html`<div class="work-list" role="list">${repeat(cards, (card) => card.key, (card) => this.renderGoalCard(card))}</div>`;
  }

  private renderRow(item: WorkItem): unknown {
    if (item.card) return this.renderCard(item);
    const classes = ['work-row', `work-${item.kind}`];
    if (item.overdue) classes.push('overdue');
    if (isSelectable(item)) classes.push('work-candidate');
    if (this.selected.has(item.key)) classes.push('selected');
    if (this.openKey === item.key) classes.push('open');
    if (item.subtask_id && this.busy.has(item.subtask_id)) classes.push('subtask-done');
    if (item.goal_check && this.busy.has(item.goal_id)) classes.push('just-completed');
    else if (this.leaving.has(item.key)) classes.push('work-leaving');
    return html`<div class=${classes.join(' ')} role="listitem" @click=${(e: MouseEvent) => this.onRowClick(e, item.key)}>
      ${this.renderLead(item)}
      <div class="work-main">
        ${this.renderTitle(item)}
        ${this.renderSub(item)}
        ${this.renderChoices(item)}${this.rowActions(item)}
      </div>
      ${this.renderDue(item)}
    </div>`;
  }

  private renderBulkBar(rows: WorkItem[]) {
    const en = this.uiLang === 'en';
    const picked = rows.filter((item) => this.selected.has(item.key));
    const chosen = picked.length;
    if (chosen === 0) {
      return html`<div class="work-bulk-bar"><button type="button" class="work-bulk-btn" @click=${() => this.emit({ action: 'bulk', bulk: 'all' })}>${en ? 'Select all' : 'すべて選択'}</button></div>`;
    }
    const labels = bulkLabels(picked, this.uiLang);
    return html`<div class="work-bulk-bar active" role="group" aria-label=${en ? 'Act on the selected' : '選んだ候補をまとめて操作'}>
      <span class="work-bulk-count" aria-live="polite">${en ? `${chosen} selected` : `${chosen}件を選択中`}</span>
      <span class="work-section-gap"></span>
      ${labels.accept ? html`<button type="button" class="work-bulk-btn work-bulk-primary" @click=${() => this.emit({ action: 'bulk', bulk: 'accept' })}>${labels.accept}</button>` : nothing}
      <button type="button" class="work-bulk-btn" @click=${() => this.emit({ action: 'bulk', bulk: 'dismiss' })}>${labels.dismiss}</button>
      <button type="button" class="work-bulk-btn work-bulk-clear" aria-label=${en ? 'Clear selection' : '選択を解除'} @click=${() => this.emit({ action: 'bulk', bulk: 'clear' })}><span class="mi mi-sm" aria-hidden="true">close</span></button>
    </div>`;
  }

  /**
   * #3064 (owner 2026-10-26): the passed provisional deadlines as one row,
   * answered together with the date offered, or opened to answer one by one.
   */
  private renderProvisionalBundle(bundle: { items: WorkItem[]; day: string }) {
    const copy = provisionalBundleText(bundle.items.length, bundle.day, this.uiLang);
    const goalIds = bundle.items.map((item) => item.goal_id);
    const label = this.uiLang === 'en' ? 'Morning check' : '朝の確認';
    return html`<div class="work-row work-judgment overdue" role="listitem">
      <span class="work-lead work-lead-icon" aria-hidden="true"><span class="mi mi-sm work-kind-icon">${WORK_KIND_ICONS.judgment}</span></span>
      <div class="work-main">
        <span class="work-title">${copy.title}</span>
        <span class="work-sub"><span class="work-sub-text"><span class="work-clock"><span class="mi mi-xs" aria-hidden="true">alarm</span>${label}</span> · ${copy.ask}</span></span>
        ${this.renderActions([
          { label: copy.all, onClick: () => this.emit({ action: 'goal-deadline-bulk', goalIds, day: bundle.day }) },
          { label: this.provisionalOpen ? copy.close : copy.open, onClick: () => { this.provisionalOpen = !this.provisionalOpen; } },
        ])}
      </div>
    </div>
    ${this.provisionalOpen ? this.renderRows(bundle.items) : nothing}`;
  }

  /**
   * Rows keyed by item, so a row keeps its element when one above it leaves:
   * a card checked right after another plays its own exit, and a click lands
   * on the row it was meant for.
   */
  private renderRows(rows: WorkItem[]) {
    return repeat(rows, (item) => item.key, (item) => this.renderRow(item));
  }

  private renderGroupRows(rows: WorkItem[]) {
    const bundle = passedProvisionalBundle(rows);
    if (!bundle) return this.renderRows(rows);
    const folded = new Set(bundle.items);
    const first = rows.indexOf(bundle.items[0]!);
    return repeat(rows, (item) => item.key, (item, i) => i === first ? this.renderProvisionalBundle(bundle) : folded.has(item) ? nothing : this.renderRow(item));
  }

  private renderGroups(rows: WorkItem[]) {
    return WORK_GROUPS.map((group) => {
      const inGroup = rows.filter((item) => (item.group || 'choose') === group);
      if (inGroup.length === 0) return nothing;
      const selectable = inGroup.filter(isSelectable);
      return html`
        <h3 class="work-group-head">${groupLabel(group, this.uiLang)}<span class="work-group-count">${inGroup.length}</span></h3>
        ${selectable.length > 0 ? this.renderBulkBar(selectable) : nothing}
        <div class="work-list" role="list">${this.renderGroupRows(inGroup)}</div>
      `;
    });
  }

  /** Cards under day headings when they span more than one day (すべて); 今日 stays one list. */
  private renderDays(cards: WorkGoalCard[]) {
    const days = dayGroups(cards, this.list!.today, (card) => cardDueRow(card).due);
    if (days.length <= 1) return this.renderCards(cards);
    return days.map(({ day, rows: inDay }) => html`
      <h3 class="work-group-head">${dayLabel(day, this.uiLang)}<span class="work-group-count">${inDay.length}</span></h3>
      ${this.renderCards(inDay)}
    `);
  }

  /** The goal cards shown under the chosen tiles: ゴール, 判断 (with the folded ones), or both. */
  private shownCards(kinds: WorkKind[]) {
    return goalCards(this.list!.items).filter((card) => {
      const kind = cardKind(card);
      return kinds.includes(kind === 'low' ? 'judgment' : kind);
    });
  }

  /**
   * 優先 (Yoshie 2026-10-28): the pressing goals, one card each, above their
   * own sections. Split into 期日超過, 期限が近い and 急ぎ, with the breakdown
   * beside the heading (Yoshie 2026-11-03, as the extension).
   */
  private renderFirst(cards: WorkGoalCard[]) {
    const first = firstCards(cards, this.list!);
    if (first.length === 0) return nothing;
    const en = this.uiLang === 'en';
    const groups = ([
      ['overdue', 'error', en ? 'Overdue' : '期日超過', en ? 'Overdue' : '期日超過'],
      ['due_soon', 'schedule', en ? 'Due soon' : '期限が近い', en ? 'Due soon' : '期限が近い'],
      ['urgent', 'priority_high', en ? 'Urgent (from what came in)' : '急ぎ（届いた内容から）', en ? 'Urgent' : '急ぎ'],
    ] as const).map(([reason, icon, label, short]) => ({ reason, icon, label, short, cards: first.filter((card) => cardFirstReason(card, this.list!) === reason) }))
      .filter((group) => group.cards.length > 0);
    return html`<section class="work-section work-section-first" aria-labelledby="workSection-first">
      <h2 class="work-section-head" id="workSection-first">
        <span class="mi mi-sm" aria-hidden="true">priority_high</span>${firstLabel(this.uiLang)}
        <span class="work-section-count">${first.length}</span>
        <span class="work-section-split work-first-splits">${groups.map((group) => html`<span class="work-first-split work-first-${group.reason}"><span class="mi mi-xs" aria-hidden="true">${group.icon}</span>${group.short}${group.cards.length}</span>`)}</span>
      </h2>
      ${groups.map((group) => html`<h3 class=${`work-group-head work-first-group work-first-${group.reason}`}><span class="mi mi-xs" aria-hidden="true">${group.icon}</span>${group.label}<span class="work-group-count">${group.cards.length}</span></h3>
        ${this.renderCards(group.cards)}`)}
    </section>`;
  }

  /**
   * A kind's section: ゴール and 判断 hold goal cards (判断 the ones asking a
   * decision, after its loose rows such as goal candidates), 対応待ち its rows.
   * The cards in 優先 are counted there.
   */
  private renderSection(kind: WorkKind, cards: WorkGoalCard[]) {
    const { main, low } = sectionRows(this.list!.items, kind);
    const rest = cards.filter((card) => cardFirstReason(card, this.list!) === null);
    const mainCards = kind === 'waiting' ? [] : rest.filter((card) => cardKind(card) === kind);
    const lowCards = kind === 'judgment' ? rest.filter((card) => cardKind(card) === 'low') : [];
    const count = main.length + mainCards.length;
    const lowCount = low.length + lowCards.length;
    if (count === 0 && lowCount === 0) return nothing;
    const s = this.list!.summary[kind];
    const en = this.uiLang === 'en';
    const id = `workSection-${kind}`;
    const overdue = [...main, ...low].filter((item) => item.overdue).length + [...mainCards, ...lowCards].filter(cardOverdue).length;
    return html`<section class=${`work-section work-section-${kind}`} aria-labelledby=${id}>
      <h2 class="work-section-head" id=${id}>
        <span class="mi mi-sm" aria-hidden="true">${WORK_KIND_ICONS[kind]}</span>${sectionLabel(kind, this.uiLang)}
        <span class="work-section-count">${count}</span>
        ${lowCount > 0 ? html`<span class="work-section-low" title=${en ? 'Low priority' : '優先度の低い判断'}>＋<span class="mi mi-xs" aria-hidden="true">low_priority</span>${lowCount}</span>` : nothing}
        ${kind === 'waiting' ? html`<span class="work-section-split">${waitingSplit(s, this.uiLang)}</span>` : nothing}
        <span class="work-section-gap"></span>
        ${overdue > 0 ? html`<span class="work-section-overdue"><span class="mi mi-xs" aria-hidden="true">error</span>${overdue} ${en ? 'overdue' : '期限切れ'}</span>` : nothing}
      </h2>
      ${main.length > 0
        ? (kind === 'judgment' ? this.renderGroups(main) : html`<div class="work-list" role="list">${this.renderRows(main)}</div>`)
        : nothing}
      ${mainCards.length > 0 ? this.renderDays(mainCards) : nothing}
      ${lowCount > 0 ? html`
        <button type="button" class="work-low-toggle" aria-expanded=${this.lowExpanded ? 'true' : 'false'} aria-controls="workLowList" @click=${() => this.emit({ action: 'toggle-low' })}>
          <span class="mi mi-xs" aria-hidden="true">${this.lowExpanded ? 'expand_more' : 'chevron_right'}</span>
          <span class="mi mi-xs" aria-hidden="true">low_priority</span>
          <span>${en ? 'Low-priority decisions' : '優先度の低い判断'}</span><span class="work-low-count">${lowCount}</span>
        </button>
        <div class="work-low-list" id="workLowList" ?hidden=${!this.lowExpanded}>${low.length > 0 ? this.renderGroups(low) : nothing}${lowCards.length > 0 ? this.renderCards(lowCards) : nothing}</div>
      ` : nothing}
    </section>`;
  }

  private renderMissing() {
    const { sources } = this.list!;
    if (sources.goals === 'ok' && sources.candidates === 'ok' && sources.holds !== 'failed') return nothing;
    const en = this.uiLang === 'en';
    const message = sources.goals === 'failed'
      ? (en ? 'Could not read your goals. Some rows are missing.' : 'ゴールを読み込めなかったため、一部が表示されていません。')
      : sources.candidates === 'failed'
        ? (en ? 'Could not read goal candidates. They are missing from the list.' : 'ゴールの候補を読み込めなかったため、表示されていません。')
        : (en ? 'Could not read new messages waiting for their goal. They are missing from the list.' : 'どのゴールの続きか確認待ちのメッセージを読み込めなかったため、表示されていません。');
    return html`<div class="work-missing" role="status"><span class="mi mi-sm" aria-hidden="true">error</span>${message}</div>`;
  }

  render() {
    if (!this.list) return nothing;
    const kinds = WORK_KINDS.filter((kind) => !this.filter || kind === this.filter || (this.filter === 'mine' && kind !== 'waiting'));
    const cards = this.shownCards(kinds);
    return html`
      <div class="work-tiles" role="group">${this.renderMineTile()}${this.renderWaitingTile()}</div>
      ${this.renderCustomerBar(this.list.items.length)}
      ${this.renderMissing()}
      ${this.renderCardBar(cards)}
      ${this.renderFirst(cards)}
      ${kinds.map((kind) => this.renderSection(kind, cards))}
    `;
  }
}

defineZenComponent('zen-work-list', ZenWorkList);
